import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ChevronLeft, Calendar as CalendarIcon, Save, Eye, Users, Upload, Image, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AGE_OPTIONS = [
  { value: 'enfants', label: 'Enfants' },
  { value: 'ados', label: 'Ados' },
  { value: 'adultes', label: 'Adultes' },
  { value: 'tous', label: 'Tout public' }
];

const TYPE_OPTIONS = [
  { value: 'jeu', label: 'Jeu de société' },
  { value: 'lecture', label: 'Lecture' },
  { value: 'tcg', label: 'TCG' },
  { value: 'atelier', label: 'Atelier' },
  { value: 'autre', label: 'Autre' }
];

export default function AdminEventForm() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!eventId;
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    short_description: '',
    date: new Date(),
    time: '14:00',
    end_time: '',
    location: 'Plume et Pion',
    max_participants: 10,
    price: 0,
    is_free: true,
    age_group: 'tous',
    event_type: 'jeu',
    image_url: ''
  });

  useEffect(() => {
    if (isEditing) {
      fetchEvent();
    }
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`);
      
      if (!response.ok) throw new Error('Événement non trouvé');
      
      const event = await response.json();
      const eventDate = new Date(event.date);
      
      setFormData({
        name: event.name || '',
        description: event.description || '',
        short_description: event.short_description || '',
        date: eventDate,
        time: format(eventDate, 'HH:mm'),
        end_time: event.end_time ? format(new Date(event.end_time), 'HH:mm') : '',
        location: event.location || 'Plume et Pion',
        max_participants: event.max_participants || 10,
        price: event.price || 0,
        is_free: event.is_free ?? true,
        age_group: event.age_group || 'tous',
        event_type: event.event_type || 'jeu',
        image_url: event.image_url || ''
      });
      
      // Set image preview if exists
      if (event.image_url) {
        setImagePreview(event.image_url);
      }
    } catch (error) {
      toast.error(error.message);
      navigate('/admin/evenements');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 Mo)');
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'upload');
      }

      const result = await response.json();
      const imageUrl = `${BACKEND_URL}/api/files/${result.file_id}`;
      
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image téléchargée avec succès');
    } catch (error) {
      toast.error(error.message);
      setImagePreview(formData.image_url || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    if (date) {
      setFormData(prev => ({ ...prev, date }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);
      
      // Combine date and time
      const [hours, minutes] = formData.time.split(':').map(Number);
      const eventDate = new Date(formData.date);
      eventDate.setHours(hours, minutes, 0, 0);

      let endDateTime = null;
      if (formData.end_time) {
        const [endHours, endMinutes] = formData.end_time.split(':').map(Number);
        endDateTime = new Date(formData.date);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        short_description: formData.short_description || null,
        date: eventDate.toISOString(),
        end_time: endDateTime ? endDateTime.toISOString() : null,
        location: formData.location,
        max_participants: parseInt(formData.max_participants),
        price: formData.is_free ? 0 : parseFloat(formData.price),
        is_free: formData.is_free,
        age_group: formData.age_group,
        event_type: formData.event_type,
        image_url: formData.image_url || null
      };

      const url = isEditing 
        ? `${BACKEND_URL}/api/admin/events/${eventId}`
        : `${BACKEND_URL}/api/admin/events`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la sauvegarde');
      }

      toast.success(isEditing ? 'Événement mis à jour' : 'Événement créé');
      navigate('/admin/evenements');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Chargement...">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#db3b17] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Modifier l\'événement' : 'Nouvel événement'}>
      <div className="max-w-4xl">
        {/* Back link */}
        <Link 
          to="/admin/evenements"
          className="inline-flex items-center text-[#645E55] hover:text-[#2D2A26] mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour aux événements
        </Link>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="event-form">
          {/* Basic info */}
          <Card className="border-2 border-[#E5E0D8] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#2D2A26]">
                Informations générales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#2D2A26] font-medium">
                  Nom de l'événement *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Soirée jeux de société"
                  required
                  className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                  data-testid="input-event-name"
                />
              </div>

              {/* Short description */}
              <div className="space-y-2">
                <Label htmlFor="short_description" className="text-[#2D2A26] font-medium">
                  Description courte
                </Label>
                <Input
                  id="short_description"
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleInputChange}
                  placeholder="Ex: Venez découvrir nos nouveautés !"
                  maxLength={150}
                  className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                  data-testid="input-short-description"
                />
                <p className="text-xs text-[#645E55]">Affichée sur la carte de l'événement (max 150 caractères)</p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-[#2D2A26] font-medium">
                  Description complète *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Décrivez votre événement en détail..."
                  required
                  rows={6}
                  className="border-2 border-[#E5E0D8] rounded-xl resize-none"
                  data-testid="input-description"
                />
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-[#2D2A26] font-medium">
                  Image de l'événement
                </Label>
                
                {/* Image preview or upload area */}
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-[#E5E0D8]">
                    <img 
                      src={imagePreview} 
                      alt="Aperçu" 
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-white/90 hover:bg-white"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Changer
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRemoveImage}
                        disabled={uploading}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {uploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#E5E0D8] rounded-xl p-8 text-center cursor-pointer hover:border-[#48b4ff] hover:bg-[#48b4ff]/5 transition-all"
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-10 h-10 text-[#48b4ff] animate-spin" />
                        <p className="text-[#645E55]">Upload en cours...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Image className="w-10 h-10 text-[#645E55]" />
                        <p className="text-[#645E55]">Cliquez pour ajouter une image</p>
                        <p className="text-xs text-[#645E55]/70">PNG, JPG, GIF (max 5 Mo)</p>
                      </div>
                    )}
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="input-image-upload"
                />
                
                {/* URL fallback option */}
                <details className="mt-2">
                  <summary className="text-sm text-[#645E55] cursor-pointer hover:text-[#2D2A26]">
                    Ou coller une URL d'image
                  </summary>
                  <Input
                    id="image_url"
                    name="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) setImagePreview(e.target.value);
                    }}
                    placeholder="https://..."
                    className="mt-2 h-10 border-2 border-[#E5E0D8] rounded-xl"
                    data-testid="input-image-url"
                  />
                </details>
              </div>
            </CardContent>
          </Card>

          {/* Date and time */}
          <Card className="border-2 border-[#E5E0D8] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#2D2A26]">
                Date et horaires
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Date picker */}
                <div className="space-y-2">
                  <Label className="text-[#2D2A26] font-medium">Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal border-2 border-[#E5E0D8] rounded-xl"
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.date ? format(formData.date, 'PPP', { locale: fr }) : 'Choisir une date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={handleDateChange}
                        locale={fr}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Start time */}
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-[#2D2A26] font-medium">Heure de début *</Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                    data-testid="input-time"
                  />
                </div>

                {/* End time */}
                <div className="space-y-2">
                  <Label htmlFor="end_time" className="text-[#2D2A26] font-medium">Heure de fin</Label>
                  <Input
                    id="end_time"
                    name="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                    data-testid="input-end-time"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location" className="text-[#2D2A26] font-medium">Lieu</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                  data-testid="input-location"
                />
              </div>
            </CardContent>
          </Card>

          {/* Participants and price */}
          <Card className="border-2 border-[#E5E0D8] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#2D2A26]">
                Participants et tarif
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Max participants */}
                <div className="space-y-2">
                  <Label htmlFor="max_participants" className="text-[#2D2A26] font-medium">
                    Nombre de places *
                  </Label>
                  <Input
                    id="max_participants"
                    name="max_participants"
                    type="number"
                    min="1"
                    max="500"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    required
                    className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                    data-testid="input-max-participants"
                  />
                </div>

                {/* Age group */}
                <div className="space-y-2">
                  <Label className="text-[#2D2A26] font-medium">Public cible *</Label>
                  <Select value={formData.age_group} onValueChange={(v) => handleSelectChange('age_group', v)}>
                    <SelectTrigger className="h-12 border-2 border-[#E5E0D8] rounded-xl" data-testid="select-age-group">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Event type */}
                <div className="space-y-2">
                  <Label className="text-[#2D2A26] font-medium">Type d'événement *</Label>
                  <Select value={formData.event_type} onValueChange={(v) => handleSelectChange('event_type', v)}>
                    <SelectTrigger className="h-12 border-2 border-[#E5E0D8] rounded-xl" data-testid="select-event-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Free event toggle */}
              <div className="flex items-center justify-between p-4 bg-[#FFF8E1] rounded-xl">
                <div>
                  <Label className="text-[#2D2A26] font-medium">Événement gratuit</Label>
                  <p className="text-sm text-[#645E55]">Désactivez pour définir un prix</p>
                </div>
                <Switch
                  checked={formData.is_free}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_free: checked }))}
                  data-testid="switch-is-free"
                />
              </div>

              {/* Price */}
              {!formData.is_free && (
                <div className="space-y-2">
                  <Label htmlFor="price" className="text-[#2D2A26] font-medium">Prix (€)</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="h-12 border-2 border-[#E5E0D8] rounded-xl"
                    data-testid="input-price"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
              data-testid="save-event-btn"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Mettre à jour' : 'Créer l\'événement'}
                </>
              )}
            </Button>
            
            {isEditing && (
              <>
                <Link to={`/evenement/${eventId}`} target="_blank">
                  <Button type="button" variant="outline" className="border-2 border-[#E5E0D8] rounded-xl">
                    <Eye className="w-4 h-4 mr-2" />
                    Voir la page
                  </Button>
                </Link>
                <Link to={`/admin/evenements/${eventId}/participants`}>
                  <Button type="button" variant="outline" className="border-2 border-[#E5E0D8] rounded-xl">
                    <Users className="w-4 h-4 mr-2" />
                    Participants
                  </Button>
                </Link>
              </>
            )}
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
